import apiClient from "../helpers/apiClient";
import { handleApiError } from "../utils/handleApiError";

// Base64 Helpers
export function arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
}

export function base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = window.atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
}

// Local Cryptography Key Management
const getLocalPrvKeyName = (userId: number | string) => `nobilis_chat_prv_key_${userId}`;
const getLocalPubKeyName = (userId: number | string) => `nobilis_chat_pub_key_${userId}`;

export async function generateAndRegisterKeyPair(userId: number): Promise<string> {
    const prvKeyName = getLocalPrvKeyName(userId);
    const pubKeyName = getLocalPubKeyName(userId);

    const savedPub = localStorage.getItem(pubKeyName);
    const savedPrv = localStorage.getItem(prvKeyName);

    if (savedPub && savedPrv) {
        // Ya existen llaves locales, intentamos registrarlas en el backend para sincronizar/verificar
        try {
            await registerPublicKey(savedPub, savedPrv);
        } catch (e) {
            console.log("Error verifying keys with backend:", e);
        }
        return savedPub;
    }

    // Si no están en localStorage, intentamos cargarlas desde el backend (para soporte multibrowser)
    try {
        const response = await apiClient.get("/chat/keys/");
        const results = response.data?.results || response.data || [];
        const registered = Array.isArray(results) ? results[0] : results;
        const regPub = registered?.publicKeyX25519 || registered?.public_key_x25519;
        const regPrv = registered?.privateKeyX25519 || registered?.private_key_x25519;

        if (regPub && regPrv) {
            localStorage.setItem(pubKeyName, regPub);
            localStorage.setItem(prvKeyName, regPrv);
            return regPub;
        }
    } catch (e) {
        console.error("Failed to fetch existing keys from backend:", e);
    }

    // Generar nuevo par de claves ECDH P-256 si no existen en ningún lado
    const keyPair = await window.crypto.subtle.generateKey(
        {
            name: "ECDH",
            namedCurve: "P-256"
        },
        true,
        ["deriveKey", "deriveBits"]
    );

    // Exportar clave privada en formato PKCS#8
    const exportedPrv = await window.crypto.subtle.exportKey("pkcs8", keyPair.privateKey);
    const prvBase64 = arrayBufferToBase64(exportedPrv);
    localStorage.setItem(prvKeyName, prvBase64);

    // Exportar clave pública en formato SPKI
    const exportedPub = await window.crypto.subtle.exportKey("spki", keyPair.publicKey);
    const pubBase64 = arrayBufferToBase64(exportedPub);
    localStorage.setItem(pubKeyName, pubBase64);

    // Registrar en el backend
    await registerPublicKey(pubBase64, prvBase64);

    return pubBase64;
}

async function registerPublicKey(publicKeyBase64: string, privateKeyBase64?: string) {
    try {
        await apiClient.post("/chat/keys/", {
            publicKeyX25519: publicKeyBase64,
            privateKeyX25519: privateKeyBase64
        });
    } catch (error) {
        // Silenciar si ya existe la llave única
    }
}

export async function getPeerPublicKey(userId: number): Promise<string | null> {
    try {
        const response = await apiClient.get(`/chat/keys/by-user/${userId}/`);
        return response.data?.publicKeyX25519 || response.data?.public_key_x25519 || null;
    } catch (error) {
        console.error("Could not fetch peer public key:", error);
        return null;
    }
}

export async function getSharedSecretKey(peerPublicKeyBase64: string, userId: number): Promise<CryptoKey> {
    const prvKeyName = getLocalPrvKeyName(userId);
    const localPrvBase64 = localStorage.getItem(prvKeyName);
    if (!localPrvBase64) {
        throw new Error("Local private key not found in storage");
    }

    const localPrivateKeyBuffer = base64ToArrayBuffer(localPrvBase64);
    const localPrivateKey = await window.crypto.subtle.importKey(
        "pkcs8",
        localPrivateKeyBuffer,
        {
            name: "ECDH",
            namedCurve: "P-256"
        },
        true,
        ["deriveKey"]
    );

    const peerPublicKeyBuffer = base64ToArrayBuffer(peerPublicKeyBase64);
    const peerPublicKey = await window.crypto.subtle.importKey(
        "spki",
        peerPublicKeyBuffer,
        {
            name: "ECDH",
            namedCurve: "P-256"
        },
        true,
        []
    );

    return await window.crypto.subtle.deriveKey(
        {
            name: "ECDH",
            public: peerPublicKey
        },
        localPrivateKey,
        {
            name: "AES-GCM",
            length: 256
        },
        true,
        ["encrypt", "decrypt"]
    );
}

// Encryption / Decryption Utilities
export async function encryptMessage(plaintext: string, sharedKey: CryptoKey) {
    const encoder = new TextEncoder();
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    
    const encryptedBuffer = await window.crypto.subtle.encrypt(
        {
            name: "AES-GCM",
            iv: iv
        },
        sharedKey,
        encoder.encode(plaintext)
    );

    const fullBytes = new Uint8Array(encryptedBuffer);
    const ciphertextBytes = fullBytes.slice(0, -16);
    const tagBytes = fullBytes.slice(-16);

    return {
        ciphertext: arrayBufferToBase64(ciphertextBytes),
        nonce: arrayBufferToBase64(iv),
        tag: arrayBufferToBase64(tagBytes)
    };
}

export async function decryptMessage(ciphertextBase64: string, nonceBase64: string, tagBase64: string, sharedKey: CryptoKey): Promise<string> {
    try {
        const decoder = new TextDecoder();
        const ciphertextBytes = base64ToArrayBuffer(ciphertextBase64);
        const nonceBytes = base64ToArrayBuffer(nonceBase64);
        const tagBytes = base64ToArrayBuffer(tagBase64);

        const fullBytes = new Uint8Array(ciphertextBytes.byteLength + tagBytes.byteLength);
        fullBytes.set(new Uint8Array(ciphertextBytes), 0);
        fullBytes.set(new Uint8Array(tagBytes), ciphertextBytes.byteLength);

        const decryptedBuffer = await window.crypto.subtle.decrypt(
            {
                name: "AES-GCM",
                iv: new Uint8Array(nonceBytes)
            },
            sharedKey,
            fullBytes
        );

        return decoder.decode(decryptedBuffer);
    } catch (e) {
        console.error("Failed to decrypt message:", e);
        return "[Mensaje cifrado - Clave incorrecta]";
    }
}

// REST API Handlers
export async function getConversations(params: { limit?: number; offset?: number } = {}) {
    try {
        const response = await apiClient.get("/chat/conversations/", { params });
        return response.data?.results || response.data || [];
    } catch (error) {
        handleApiError(error, {});
        return [];
    }
}

/**
 * Detalle de UNA conversacion. Se usa para releerla despues de una accion que cambia el estado del
 * hilo — reagendar, aceptar — porque ese estado viaja anidado en la conversacion
 * (`expertiseRequestDetail`), no en los mensajes.
 */
export async function getConversation(conversationUuid: string) {
    try {
        const response = await apiClient.get(`/chat/conversations/${conversationUuid}/`);
        return response.data;
    } catch (error) {
        handleApiError(error, {});
        return null;
    }
}

export async function getOrCreateConversation(chatType: string, entityId: number, experienceDateId?: number) {
    try {
        const payload: any = { chat_type: chatType, entity_id: entityId };
        if (experienceDateId) {
            payload.experience_date_id = experienceDateId;
        }
        const response = await apiClient.post("/chat/conversations/get-or-create/", payload);
        return response.data;
    } catch (error) {
        handleApiError(error, {});
        return null;
    }
}

export async function getMessages(conversationUuid: string, nextUrl?: string | null) {
    try {
        const url = nextUrl ? nextUrl : `/chat/messages/?conversation_uuid=${conversationUuid}`;
        const response = await apiClient.get(url);
        return {
            results: response.data?.results || response.data || [],
            next: response.data?.next || null
        };
    } catch (error) {
        handleApiError(error, {});
        return { results: [], next: null };
    }
}

export async function sendMessage(conversationUuid: string, payload: {
    ciphertext?: string;
    nonce?: string;
    tag?: string;
    cleartextContent?: string;
    cleartext_content?: string;
    replyTo?: number;
    reply_to?: number;
    links?: string[];
}, files?: File[]) {
    try {
        const formData = new FormData();
        formData.append("conversation_uuid", conversationUuid);
        if (payload.ciphertext) formData.append("ciphertext", payload.ciphertext);
        if (payload.nonce) formData.append("nonce", payload.nonce);
        if (payload.tag) formData.append("tag", payload.tag);
        
        const cleartext = payload.cleartextContent || payload.cleartext_content;
        if (cleartext) formData.append("cleartext_content", cleartext);

        const reply = payload.replyTo || payload.reply_to;
        if (reply) formData.append("reply_to", String(reply));

        if (payload.links) {
            payload.links.forEach((link) => {
                formData.append("links", link);
            });
        }

        if (files) {
            files.forEach((f) => {
                formData.append("files", f);
            });
        }

        const response = await apiClient.post("/chat/messages/", formData, {
            headers: {
                "Content-Type": "multipart/form-data"
            }
        });
        return response.data;
    } catch (error) {
        handleApiError(error, {});
        return null;
    }
}

export async function reactToMessage(messageId: number, emoji: string) {
    try {
        const response = await apiClient.post(`/chat/messages/${messageId}/react/`, { emoji });
        return response.data;
    } catch (error) {
        handleApiError(error, {});
        return null;
    }
}

export async function deleteMessage(messageId: number) {
    try {
        const response = await apiClient.delete(`/chat/messages/${messageId}/`);
        return response.data;
    } catch (error) {
        handleApiError(error, {});
        return null;
    }
}

export async function leaveConversation(conversationUuid: string) {
    try {
        const response = await apiClient.post(`/chat/conversations/${conversationUuid}/leave/`);
        return response.data;
    } catch (error) {
        handleApiError(error, {});
        return null;
    }
}

export async function markConversationAsRead(conversationUuid: string) {
    try {
        const response = await apiClient.post(`/chat/conversations/${conversationUuid}/mark-as-read/`);
        return response.data;
    } catch (error) {
        handleApiError(error, {});
        return null;
    }
}

export async function updateMessage(messageId: number, payload: {
    ciphertext?: string;
    nonce?: string;
    tag?: string;
    cleartextContent?: string;
    cleartext_content?: string;
}) {
    try {
        const data: any = {};
        if (payload.ciphertext) data.ciphertext = payload.ciphertext;
        if (payload.nonce) data.nonce = payload.nonce;
        if (payload.tag) data.tag = payload.tag;
        
        const cleartext = payload.cleartextContent || payload.cleartext_content;
        if (cleartext) data.cleartext_content = cleartext;

        const response = await apiClient.patch(`/chat/messages/${messageId}/`, data);
        return response.data;
    } catch (error) {
        handleApiError(error, {});
        return null;
    }
}

export async function updateCommunityComment(commentId: number, content: string) {
    try {
        const response = await apiClient.patch(`/community/comments/${commentId}/`, { content });
        return response.data;
    } catch (error) {
        handleApiError(error, {});
        return null;
    }
}

export async function deleteCommunityComment(commentId: number) {
    try {
        const response = await apiClient.delete(`/community/comments/${commentId}/`);
        return response.data;
    } catch (error) {
        handleApiError(error, {});
        return null;
    }
}

